import { useEffect, useId, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  buildFlttRankingChartData,
  canShowFlttRankingChart,
  formatPointsValue,
  formatRankingPlace,
} from "../utils/flttRankingDisplay";

const LIGHT_COLORS = {
  points: "#0877bc",
  place: "#1f6b4a",
  muted: "#6b7280",
  grid: "rgba(8, 119, 188, 0.14)",
  cursor: "rgba(8, 119, 188, 0.35)",
  legend: "#334155",
  pointsFillTop: 0.35,
};

const DARK_COLORS = {
  points: "#5ec8f5",
  place: "#6ee7a0",
  muted: "#cbd5e1",
  grid: "rgba(94, 200, 245, 0.28)",
  cursor: "rgba(94, 200, 245, 0.45)",
  legend: "#e2e8f0",
  pointsFillTop: 0.45,
};

function isDarkModeUi() {
  if (typeof document === "undefined") return false;
  return (
    document.documentElement.classList.contains("dark-mode") ||
    document.body.classList.contains("dark-mode")
  );
}

function useFlttChartColors(tone) {
  const [colors, setColors] = useState(() =>
    tone === "dark" || (tone !== "light" && isDarkModeUi())
      ? DARK_COLORS
      : LIGHT_COLORS
  );

  useEffect(() => {
    if (tone === "dark") {
      setColors(DARK_COLORS);
      return undefined;
    }
    if (tone === "light") {
      setColors(LIGHT_COLORS);
      return undefined;
    }
    const sync = () => {
      setColors(isDarkModeUi() ? DARK_COLORS : LIGHT_COLORS);
    };
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, [tone]);

  return colors;
}

function ChartTooltip({ active, payload, t }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  const points = formatPointsValue(row.points);
  const place = formatRankingPlace(row.place);

  return (
    <div className="fltt-chart__tooltip">
      <div className="fltt-chart__tooltip-week">
        {row.weekLabel}
        {row.classement ? ` · ${row.classement}` : ""}
      </div>
      {points != null ? (
        <div className="fltt-chart__tooltip-row">
          <span className="fltt-chart__swatch fltt-chart__swatch--points" />
          <span>{t("flttSlipPoints", "Points")}</span>
          <strong>
            {points} {t("flttPtsShort", "pts")}
          </strong>
        </div>
      ) : null}
      {place != null ? (
        <div className="fltt-chart__tooltip-row">
          <span className="fltt-chart__swatch fltt-chart__swatch--place" />
          <span>{t("flttSlipNational", "National")}</span>
          <strong>{place}</strong>
        </div>
      ) : null}
    </div>
  );
}

function paddedDomain(values, { reverse = false, minPad = 1 } = {}) {
  const nums = values.filter((v) => v != null && Number.isFinite(v));
  if (!nums.length) return reverse ? [1, 0] : [0, 1];
  let min = Math.min(...nums);
  let max = Math.max(...nums);
  if (min === max) {
    const pad = Math.max(minPad, Math.abs(min) * 0.05 || minPad);
    min -= pad;
    max += pad;
  } else {
    const pad = Math.max(minPad, (max - min) * 0.12);
    min -= pad;
    max += pad;
  }
  if (reverse) return [Math.ceil(max), Math.max(0, Math.floor(min))];
  return [Math.max(0, Math.floor(min)), Math.ceil(max)];
}

/**
 * Dual-axis chart: ranking points (area) + national place (line, inverted so climbing rises).
 */
export function FlttRankingHistoryChart({ history = [], t, locale, tone }) {
  const colors = useFlttChartColors(tone);
  const data = useMemo(
    () => buildFlttRankingChartData(history, locale),
    [history, locale]
  );
  const show = canShowFlttRankingChart(history);
  const hasPoints = data.some((d) => d.points != null);
  const hasPlace = data.some((d) => d.place != null);

  const pointsDomain = useMemo(
    () => paddedDomain(data.map((d) => d.points)),
    [data]
  );
  const placeDomain = useMemo(
    () => paddedDomain(data.map((d) => d.place), { reverse: true, minPad: 2 }),
    [data]
  );

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const reactId = useId().replace(/:/g, "");
  const gradientId = `flttPointsFill-${reactId}`;

  if (!show) return null;

  return (
    <div
      className="fltt-chart"
      role="img"
      aria-label={t("flttRankingChartAria", "Ranking points and national place over time")}
    >
      <div className="fltt-chart__head">
        <span className="fltt-chart__title">
          {t("flttRankingChartTitle", "Classement trend")}
        </span>
        <span className="fltt-chart__subtitle">
          {t("flttRankingChartSubtitle", "Points & national place")}
        </span>
      </div>
      <div className="fltt-chart__canvas">
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={colors.points}
                  stopOpacity={colors.pointsFillTop}
                />
                <stop offset="100%" stopColor={colors.points} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={colors.grid} vertical={false} />
            <XAxis
              dataKey="weekLabel"
              tick={{ fill: colors.muted, fontSize: 10, fontWeight: 600 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              minTickGap={28}
            />
            {hasPoints ? (
              <YAxis
                yAxisId="points"
                domain={pointsDomain}
                width={36}
                tick={{ fill: colors.points, fontSize: 10, fontWeight: 650 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
            ) : null}
            {hasPlace ? (
              <YAxis
                yAxisId="place"
                orientation="right"
                domain={placeDomain}
                width={36}
                tick={{ fill: colors.place, fontSize: 10, fontWeight: 650 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                tickFormatter={(v) => `#${Math.round(v)}`}
              />
            ) : null}
            <Tooltip
              content={<ChartTooltip t={t} />}
              cursor={{
                stroke: colors.cursor,
                strokeWidth: 1,
              }}
            />
            <Legend
              verticalAlign="top"
              align="right"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{
                fontSize: 11,
                fontWeight: 650,
                paddingBottom: 4,
                color: colors.legend,
              }}
            />
            {hasPoints ? (
              <Area
                yAxisId="points"
                type="monotone"
                dataKey="points"
                name={t("flttSlipPoints", "Points")}
                stroke={colors.points}
                strokeWidth={2.2}
                fill={`url(#${gradientId})`}
                connectNulls
                isAnimationActive={!reduceMotion}
                dot={{ r: 2.5, strokeWidth: 0, fill: colors.points }}
                activeDot={{ r: 4.5 }}
              />
            ) : null}
            {hasPlace ? (
              <Line
                yAxisId="place"
                type="monotone"
                dataKey="place"
                name={t("flttSlipNational", "National")}
                stroke={colors.place}
                strokeWidth={2.2}
                connectNulls
                isAnimationActive={!reduceMotion}
                dot={{ r: 2.5, strokeWidth: 0, fill: colors.place }}
                activeDot={{ r: 4.5 }}
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
